import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  type Notification as AbstractNotification,
  type Types,
  TypesEnum,
} from '@svoi-ui/interfaces/notification'
import { Notification } from '~/models/notification'
import { exhaustiveCheck } from '~/shared/utils/helpers'

type NotificationPayload = Omit<AbstractNotification, 'id'> | string

function getNotificationTitle(type: Types): string {
  switch (type) {
    case TypesEnum.error:
      return 'Ошибка'
    case TypesEnum.warning:
      return 'Предупреждение'
    case TypesEnum.info:
    case TypesEnum.infoBlue:
      return 'Информация'
    default:
      return exhaustiveCheck(type, 'Unknown notification type')
  }
}

function createNotification(
  params: NotificationPayload,
  type: Types,
  titleOverride?: string
): Notification {
  if (typeof params === 'string') {
    return new Notification({
      title: titleOverride ?? getNotificationTitle(type),
      text: params,
      type,
    })
  }

  return new Notification({ ...params, type })
}

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<Array<AbstractNotification>>([])

  const setNotifications = (value: Array<AbstractNotification>) => {
    notifications.value = value
  }

  const pushNotification = (params: AbstractNotification) => {
    notifications.value.push(params)
  }

  const pushInfo = (params: NotificationPayload) => {
    pushNotification(createNotification(params, TypesEnum.info))
  }

  const pushInfoBlue = (params: NotificationPayload) => {
    pushNotification(createNotification(params, TypesEnum.infoBlue))
  }

  const pushSuccess = (params: NotificationPayload) => {
    pushNotification(createNotification(params, TypesEnum.info, 'Успех'))
  }

  const pushWarning = (params: NotificationPayload) => {
    pushNotification(createNotification(params, TypesEnum.warning))
  }

  const pushError = (params: NotificationPayload) => {
    pushNotification(createNotification(params, TypesEnum.error))
  }

  const setError = (params: NotificationPayload) => {
    const text = typeof params === 'string' ? params : params.text
    if (notifications.value.some(n => n.type === TypesEnum.error && n.text === text)) return
    pushError(params)
  }

  return {
    notifications,
    setNotifications,
    pushNotification,
    pushInfo,
    pushInfoBlue,
    pushSuccess,
    pushWarning,
    pushError,
    setError,
  }
})
